import { useCallback, useEffect, useRef, useState } from "react";

import { listRequestMessages, sendRequestMessage } from "../api/verifications";
import { C, font } from "../styles/tokens";
import { InlineIcon } from "./InlineIcon";

/**
 * Scoped message thread attached to a verification request (client
 * requirement 3). Both sides -- the requesting professional and officers of
 * the verifying org/institution -- see the same thread; the backend enforces
 * that nobody else can. Polls every 15s while open (matches the app's
 * poll-based notification model; no websockets).
 */
export function MessageThreadDialog({ requestId, title, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const items = await listRequestMessages(requestId);
      setMessages(Array.isArray(items) ? items : []);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not load messages",
      );
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    load();
    const handle = setInterval(load, 15_000);
    return () => clearInterval(handle);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await sendRequestMessage(requestId, text.trim());
      setText("");
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message ?? err?.message ?? "Could not send",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: C.bg,
          borderRadius: 10,
          width: "min(480px, 100%)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "min(600px, 90vh)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
          fontFamily: font,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: `1px solid ${C.divider}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark }}>
              Messages
            </div>
            {title && (
              <div
                style={{
                  fontSize: 12,
                  color: C.textMed,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 20,
              color: C.textMed,
              cursor: "pointer",
              lineHeight: 1,
              padding: 4,
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>

        {/* Thread */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "14px 18px",
            minHeight: 180,
          }}
        >
          {loading ? (
            <div
              style={{
                fontSize: 13,
                color: C.textMed,
                textAlign: "center",
                padding: "24px 0",
              }}
            >
              Loading...
            </div>
          ) : messages.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: C.textMed,
                textAlign: "center",
                padding: "24px 0",
              }}
            >
              No messages yet. Questions about this verification go here. Only
              you and the other party can see them.
            </div>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  justifyContent: m.isMine ? "flex-end" : "flex-start",
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    maxWidth: "80%",
                    background: m.isMine ? C.primaryLight : C.bgMuted,
                    borderRadius: 10,
                    padding: "8px 12px",
                  }}
                >
                  {!m.isMine && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.textMed,
                        marginBottom: 2,
                      }}
                    >
                      {m.senderName}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 13,
                      color: C.textDark,
                      whiteSpace: "pre-line",
                      lineHeight: 1.5,
                    }}
                  >
                    {m.body}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: C.textLight,
                      marginTop: 3,
                      textAlign: "right",
                    }}
                  >
                    {formatTime(m.createdAt)}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {error && (
          <div style={{ padding: "6px 18px", fontSize: 12, color: C.red }}>
            {error}
          </div>
        )}

        {/* Composer */}
        <form
          onSubmit={handleSend}
          style={{
            display: "flex",
            gap: 8,
            padding: "12px 18px",
            borderTop: `1px solid ${C.divider}`,
          }}
        >
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a message..."
            maxLength={2000}
            style={{
              flex: 1,
              padding: "10px 12px",
              fontSize: 13,
              fontFamily: font,
              border: `1px solid ${C.dividerStrong}`,
              borderRadius: 8,
              color: C.textDark,
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            style={{
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: 700,
              color: "#fff",
              background: sending || !text.trim() ? C.textLight : C.primary,
              border: 0,
              borderRadius: 8,
              cursor: sending || !text.trim() ? "default" : "pointer",
              fontFamily: font,
            }}
          >
            {sending ? "..." : "Send"}
          </button>
        </form>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
